import React, { type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../src/lib/utils';
const SERIF = {
  fontFamily: 'var(--font-serif)'
} as const;

/** Signal palette — each colour carries meaning across the whole deck. */
export const INK = {
  blue: '#3B8CFF',
  emerald: '#2FBE8F',
  violet: '#9B7BFF',
  gold: '#D8A55A',
  grey: '#8B95A8'
} as const;
export function Eyebrow({
  children,
  className,
  tone = INK.blue
}: {
  children: ReactNode;
  className?: string;
  tone?: string;
}) {
  return <p className={cn('flex items-center gap-[0.9cqw] text-[clamp(8px,0.82cqw,17px)] font-medium uppercase tracking-[0.3em]', className)} style={{
    color: tone
  }} data-id="element-37" data-mp="32p9xv695jo32">
      <span aria-hidden className="inline-block h-px w-[2.2cqw]" style={{
      background: tone
    }} data-id="element-38" data-mp="3cklclokk8vat" />
      {children}
    </p>;
}

/** Cover-scale display type. */
export function Display({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <h1 className={cn('text-[clamp(36px,5.4cqw,116px)] font-normal leading-[0.98] tracking-[-0.02em] text-[#F4F7FC]', className)} style={SERIF} data-id="element-39" data-mp="3uhn3io4nxva4">
      {children}
    </h1>;
}

/** Takeaway slide title. */
export function Title({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <h2 className={cn('text-[clamp(20px,2.55cqw,54px)] font-normal leading-[1.12] tracking-[-0.01em] text-[#F4F7FC]', className)} style={SERIF} data-id="element-40" data-mp="3bq2oyq1d7nfp">
      {children}
    </h2>;
}
export function Body({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <p className={cn('text-[clamp(11px,1.18cqw,25px)] leading-[1.55] text-[#C3CBD9]', className)} data-id="element-41" data-mp="3phqziovyu3uk">
      {children}
    </p>;
}
export function Caption({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <p className={cn('text-[clamp(9px,0.96cqw,20px)] leading-[1.5] text-[#8B95A8]', className)} data-id="element-42" data-mp="44dbxkkm7z91">
      {children}
    </p>;
}
export function Label({
  children,
  className,
  tone
}: {
  children: ReactNode;
  className?: string;
  tone?: string;
}) {
  return <span className={cn('text-[clamp(7.5px,0.76cqw,16px)] uppercase tracking-[0.24em]', className)} style={{
    color: tone ?? '#7C8699'
  }} data-id="element-43" data-mp="8wwsmqelnai2">
      {children}
    </span>;
}

/** Large numeric typography for headline metrics. */
export function Metric({
  value,
  label,
  tone = '#F4F7FC',
  size = 'md',
  className
}: {
  value: string;
  label: string;
  tone?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const sizes = {
    sm: 'text-[clamp(18px,2cqw,42px)]',
    md: 'text-[clamp(24px,2.9cqw,62px)]',
    lg: 'text-[clamp(34px,4.4cqw,94px)]'
  } as const;
  return <div className={className} data-id="element-44" data-mp="10px3xtfshly8">
      <p className={cn('leading-[0.95] tracking-[-0.01em]', sizes[size])} style={{
      ...SERIF,
      color: tone
    }} data-id="element-45" data-mp="194bmr6e5sy6y">
        {value}
      </p>
      <p className="mt-[1.1cqh] text-[clamp(8px,0.84cqw,17px)] uppercase tracking-[0.18em] text-[#7C8699]" data-id="element-46" data-mp="1d3tv72osngsw">
        {label}
      </p>
    </div>;
}
export function Panel({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('border border-white/10 bg-white/[0.028] backdrop-blur-[2px]', className)} data-id="element-47" data-mp="1rwdu1r4wv24j">
      {children}
    </div>;
}

/**
 * Subtle entrance motion for SECONDARY content only — takeaway titles and hero
 * numbers must be legible on first paint, so never wrap those.
 */
export function Reveal({
  children,
  delay = 0,
  className
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  if (reduced) return <div className={className} data-id="element-48" data-mp="2fm85c49l0imo">{children}</div>;
  return <motion.div className={className} initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-49" data-mp="qzc9jq3v4jrg">
      {children}
    </motion.div>;
}